import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useMemo, useEffect } from 'react'
import styled from 'styled-components'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import { ThemeItem } from 'tripPlanner/hooks/api/tripGenerationData'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import * as Analytics from 'analytics/analytics'

const LoadingContainer = styled(VerticalSpacer)`
  padding-block: ${rem(24)};
`

interface Props {
  themes: Array<ThemeItem> | undefined
  themesLoading: boolean
  selectedThemeNames: Array<string>
  onThemeToggle: (theme: string) => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

const fallBackThemes: Array<ThemeItem> = [
  {
    theme: 'Tourist highlights',
    description: '⭐',
  },
  {
    theme: 'Beach days',
    description: '🏖️',
  },
  {
    theme: 'Coastal walks',
    description: '🌊',
  },
  {
    theme: 'Harbour cruises',
    description: '🚢',
  },
  {
    theme: 'Local food & wine',
    description: '🍷',
  },
  {
    theme: 'Outdoor adventure',
    description: '🥾',
  },
  {
    theme: 'Arts & Museums',
    description: '🖼️',
  },
  {
    theme: 'Neighbourhood exploring',
    description: '🗺️',
  },
  {
    theme: 'Shopping',
    description: '🛍️',
  },
]

function AITripThemeSelection(props: Props) {
  const {
    themes,
    themesLoading,
    selectedThemeNames,
    onThemeToggle,
    isGeneratingTrip,
    stepNumber,
    totalSteps,
  } = props
  const selectedThemeSet = useMemo(() =>
    new Set(selectedThemeNames),
  [selectedThemeNames])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'themes',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepNumber} of {totalSteps}</BodyText>
        <Heading variant="heading1">
          Trip <i>Vibes</i>
        </Heading>
        <BodyText variant="large">
          Pick the experiences that excite you! Let’s set the vibe just right for you.
        </BodyText>
      </VerticalSpacer>

      {themesLoading && (
        <LoadingContainer gap={24}>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
        </LoadingContainer>
      )}

      {!themesLoading && (
        <TileCardsGroup>
          {(themes ?? fallBackThemes).map((item) => (
            <TileCard
              key={item.theme}
              title={item.theme}
              icon={item.description}
              value={item.theme}
              onClick={onThemeToggle}
              selected={selectedThemeSet.has(item.theme)}
              disabled={isGeneratingTrip}
            />
          ))}
        </TileCardsGroup>
      )}
    </Group>
  )
}

export default AITripThemeSelection
